/* eslint-disable sonarjs/cognitive-complexity */
import { useRouter } from 'next/router';

import { CHANNEL } from '@boss/constants/b2b-b2c';
import { trackEvents } from '@boss/constants/shared';
import { Tracker } from '@boss/utils';

import { useCountry, useProfile } from '../';

export const useEventTracker = (componentName?: string) => {
  const profile = useProfile();
  const { locale } = useRouter();
  const { countryCode } = useCountry();

  // Create a function to get fresh commonFields each time
  const getCommonFields = () => {
    try {
      // Don't track if profile is still loading to avoid inconsistent states
      if (profile.status === 'loading') {
        return null;
      }

      return {
        language: locale,
        visitorLoginStatus: profile.isLoggedIn ? 'logged-in' : 'logged-out',
        userId: profile.accountId,
        brand: CHANNEL ?? '',
      };
    } catch (error) {
      console.error(`Error in getCommonFields from ${componentName || 'unknown component'}:`, error);
      return null;
    }
  };

  const ECOMMERCE_EVENTS = [
    trackEvents.ADD_TO_CART,
    trackEvents.ADD_TO_WISHLIST,
    trackEvents.BEGIN_CHECKOUT,
    trackEvents.CHECKOUT,
    trackEvents.PURCHASE,
    trackEvents.SELECT_ITEM,
    trackEvents.VIEW_CART,
    trackEvents.VIEW_ITEM,
    trackEvents.VIEW_LIST,
  ] as const;

  type TrackingProps = {
    item_name?: string;
    productName?: string;
  } & Record<string, unknown>;

  const formatEcommerceItem = (props: TrackingProps, event: string) => {
    const commonFields = getCommonFields();

    if (!commonFields) {
      return {};
    }

    const baseItem = {
      item_id: props.item_id || props.productId,
      item_id_detail: props.item_id_detail || props.articleId,
      item_name_detail: props.item_name_detail || props.articleName,
      item_name: (props.item_name || props.productName)?.toLowerCase(),
      affiliation: `${locale}-${countryCode} webshop`,
      index: props.index || 0,
      item_brand: commonFields.brand,
      item_category: props.item_category || props.productCategory,
      item_category2: props.item_category2 || '',
      item_category3: props.item_category3 || '',
      item_category4: props.item_category4 || '',
      item_category5: props.item_category5 || '',
      item_list_id: props.item_list_id || '',
      item_list_name: props.item_list_name || '',
      price: props.price || 0,
      quantity: props.quantity || null,
    };

    if (event === trackEvents.ADD_TO_CART) {
      return {
        ...baseItem,
        item_id_detail: props.item_id_detail || props.articleId,
        item_name_detail: props.item_name_detail || props.articleName,
        price: props.price || 0,
        quantity: props.quantity || 1,
      };
    }

    return baseItem;
  };

  const trackEcommerceEvent = (event: string, props: TrackingProps) => {
    const commonFields = getCommonFields();

    if (!commonFields) {
      return;
    }

    // Clear the previous ecommerce object
    window.dataLayer?.push({ ecommerce: null });

    const items = Array.isArray(props.items)
      ? props.items.map(item => formatEcommerceItem(item, event))
      : [formatEcommerceItem(props, event)];

    const totalValue = event === trackEvents.ADD_TO_CART ? props.totalPrice || props.value || 0 : 0;
    const currency = 'EUR';

    window.dataLayer?.push({
      event,
      ecommerce: {
        ...(event === trackEvents.ADD_TO_CART && {
          currency,
          value: totalValue,
        }),
        ...((event === trackEvents.BEGIN_CHECKOUT || event === trackEvents.VIEW_CART) && {
          currency,
          value: props.value,
        }),
        ...((event === trackEvents.PURCHASE || event === trackEvents.CHECKOUT) && {
          currency,
          value: props.value || 0,
          transaction_id: props.transaction_id,
        }),
        items,
      },
      ...commonFields,
    });
  };

  const trackCustomEvent = (event: string, props: TrackingProps, includePageInfo = false) => {
    const commonFields = getCommonFields();

    if (!commonFields) {
      return;
    }

    if (ECOMMERCE_EVENTS.includes(event)) {
      trackEcommerceEvent(event, props);
    } else {
      // Create tracker with fresh commonFields each time
      const tracker = new Tracker(commonFields);

      tracker.trackCustomEvent(
        {
          event,
          ...props,
        },
        includePageInfo,
      );
    }
  };

  const trackPageView = (pageType: string) => {
    const commonFields = getCommonFields();

    if (!commonFields) {
      return;
    }

    // Create tracker with fresh commonFields each time
    const tracker = new Tracker(commonFields);

    return tracker.trackPageView({
      pageType,
    });
  };

  const trackLinkClick = (props: Record<string, unknown>) => {
    const commonFields = getCommonFields();

    if (!commonFields) {
      return;
    }

    // Create tracker with fresh commonFields each time
    const tracker = new Tracker(commonFields);

    return tracker.trackLinkClick({
      ...props,
    });
  };

  return {
    trackPageView,
    trackLinkClick,
    trackCustomEvent,
  };
};
